import React, { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Minus, Plus, Trash2 } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { useIcd10Cache, useNhrplCache } from '../hooks/useCodeCache';
import { CodeItem, CodeSelector } from '../components/CodeSelector';
import { Invoice, LineItem } from '../types/invoice';
import { Consumable } from '../types/consumable';
import { Patient } from '../types/patient';

export function CreateInvoicePage() {
  const navigate = useNavigate();
  const { get, post } = useApi();
  const [searchParams] = useSearchParams();
  const prefillPatientId = searchParams.get('patientId') || '';

  const { search: searchIcd10, loading: icd10Loading } = useIcd10Cache();
  const { search: searchNhrpl, loading: nhrplLoading } = useNhrplCache();

  // Patient
  const [patientSearch, setPatientSearch] = useState('');
  const [patients, setPatients] = useState<Patient[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);

  // Codes
  const [icd10Codes, setIcd10Codes] = useState<CodeItem[]>([]);
  const [nhrplCodes, setNhrplCodes] = useState<CodeItem[]>([]);

  // Consumables
  const [availableConsumables, setAvailableConsumables] = useState<Consumable[]>([]);
  const [selectedConsumables, setSelectedConsumables] = useState<{ consumable: Consumable; quantity: number }[]>([]);

  // Form
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Prefill patient
  useEffect(() => {
    if (!prefillPatientId) return;
    get<Patient>(`/patients/${prefillPatientId}`).then(r => {
      if (r.success && r.data) setSelectedPatient(r.data);
    });
  }, [prefillPatientId, get]);

  // Load consumables
  useEffect(() => {
    get<Consumable[]>('/consumables').then(r => {
      if (r.success && r.data) setAvailableConsumables(r.data);
    });
  }, [get]);

  // Patient search
  useEffect(() => {
    if (patientSearch.length < 2 || selectedPatient) { setPatients([]); return; }
    const timer = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const r = await get<Patient[]>(`/patients?search=${encodeURIComponent(patientSearch)}&activeOnly=true`);
        if (r.success && r.data) setPatients(r.data);
      } catch { /* ignore */ }
      setSearchLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [patientSearch, selectedPatient, get]);

  // Build line items from NHRPL codes + consumables
  const lineItems: LineItem[] = useMemo(() => {
    const nhrplItems: LineItem[] = nhrplCodes.map(c => ({
      code: c.code,
      description: c.description,
      codeType: 'nhrpl' as const,
      quantity: 1,
      unitPrice: c.tariffAmount || 0,
      total: c.tariffAmount || 0,
    }));
    const consumableItems: LineItem[] = selectedConsumables.map(sc => ({
      code: sc.consumable.id,
      description: `${sc.consumable.name} (${sc.consumable.unit})`,
      codeType: 'consumable' as 'nhrpl', // stored as generic line item
      quantity: sc.quantity,
      unitPrice: sc.consumable.unitPrice,
      total: sc.quantity * sc.consumable.unitPrice,
    }));
    return [...nhrplItems, ...consumableItems];
  }, [nhrplCodes, selectedConsumables]);

  const subtotal = useMemo(() => lineItems.reduce((s, i) => s + i.total, 0), [lineItems]);

  const handleSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    if (!selectedPatient) { setError('Select a patient'); return; }
    if (lineItems.length === 0) { setError('Add at least one procedure code or consumable'); return; }

    setError('');
    setSubmitting(true);
    try {
      // Build combined line items: ICD-10 codes as diagnosis lines (price 0) + NHRPL as procedure lines
      const allItems: LineItem[] = [
        ...icd10Codes.map(c => ({
          code: c.code,
          description: c.description,
          codeType: 'icd10' as const,
          quantity: 1,
          unitPrice: 0,
          total: 0,
        })),
        ...lineItems,
      ];

      const result = await post<Invoice>('/invoices', {
        patientId: selectedPatient.id,
        lineItems: allItems,
        notes,
      });

      if (result.success && result.data) {
        navigate(`/billing/${result.data.id}`);
      } else {
        setError(result.error || 'Failed to create invoice');
      }
    } catch (err: unknown) {
      if (axios.isAxiosError(err) && err.response?.data?.error) setError(err.response.data.error);
      else setError('Failed to create invoice');
    } finally { setSubmitting(false); }
  }, [selectedPatient, lineItems, icd10Codes, notes, post, navigate]);

  return (
    <main className="max-w-3xl mx-auto px-4 py-8">
      <h1 className="text-xl font-bold text-gray-900 mb-6">New Invoice</h1>

      {error && <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Patient selector */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Patient *</label>
          {selectedPatient ? (
            <div className="flex items-center justify-between px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
              <span className="text-sm font-medium text-blue-900">
                {selectedPatient.title} {selectedPatient.firstName} {selectedPatient.lastName}
              </span>
              <button type="button" onClick={() => { setSelectedPatient(null); setPatientSearch(''); }}
                className="text-blue-600 hover:text-blue-800 text-xs">Change</button>
            </div>
          ) : (
            <div className="relative">
              <input type="text" value={patientSearch} onChange={e => setPatientSearch(e.target.value)}
                placeholder="Search by name or ID..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" autoFocus />
              {searchLoading && <Loader2 className="absolute right-3 top-2.5 h-4 w-4 animate-spin text-gray-400" />}
              {patients.length > 0 && (
                <ul className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                  {patients.map(p => (
                    <li key={p.id}>
                      <button type="button"
                        onClick={() => { setSelectedPatient(p); setPatients([]); setPatientSearch(''); }}
                        className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm">
                        <span className="font-medium">{p.title} {p.firstName} {p.lastName}</span>
                        <span className="text-gray-500 ml-2">{p.idNumber}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* ICD-10 diagnosis codes */}
        <CodeSelector
          codeType="icd10"
          selected={icd10Codes}
          onChange={setIcd10Codes}
          searchFn={searchIcd10}
          cacheLoading={icd10Loading}
          label="Diagnosis Codes (ICD-10)"
          placeholder="Search ICD-10 codes..."
        />

        {/* NHRPL procedure codes */}
        <CodeSelector
          codeType="nhrpl"
          selected={nhrplCodes}
          onChange={setNhrplCodes}
          searchFn={searchNhrpl}
          cacheLoading={nhrplLoading}
          label="Procedure Codes (NHRPL) *"
          placeholder="Search NHRPL procedure codes..."
        />

        {/* Consumables selector */}
        {availableConsumables.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Consumables</label>
            <div className="space-y-2">
              {availableConsumables.map(c => {
                const sel = selectedConsumables.find(sc => sc.consumable.id === c.id);
                return (
                  <div key={c.id} className="flex items-center gap-3 text-sm">
                    <button
                      type="button"
                      onClick={() => {
                        if (sel) {
                          setSelectedConsumables(prev => prev.filter(sc => sc.consumable.id !== c.id));
                        } else {
                          setSelectedConsumables(prev => [...prev, { consumable: c, quantity: 1 }]);
                        }
                      }}
                      className={`px-2 py-1 rounded border text-xs font-medium ${
                        sel ? 'bg-blue-100 border-blue-300 text-blue-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {sel ? 'Added' : 'Add'}
                    </button>
                    <span className="flex-1 text-gray-900">{c.name}</span>
                    <span className="text-gray-500">R {c.unitPrice.toFixed(2)} / {c.unit}</span>
                    {sel && (
                      <div className="flex items-center gap-1">
                        <button type="button" onClick={() => setSelectedConsumables(prev =>
                          prev.map(sc => sc.consumable.id === c.id && sc.quantity > 1
                            ? { ...sc, quantity: sc.quantity - 1 } : sc)
                        )} className="p-0.5 rounded hover:bg-gray-200"><Minus className="h-3.5 w-3.5" /></button>
                        <span className="w-6 text-center font-medium">{sel.quantity}</span>
                        <button type="button" onClick={() => setSelectedConsumables(prev =>
                          prev.map(sc => sc.consumable.id === c.id
                            ? { ...sc, quantity: sc.quantity + 1 } : sc)
                        )} className="p-0.5 rounded hover:bg-gray-200"><Plus className="h-3.5 w-3.5" /></button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Line items preview */}
        {lineItems.length > 0 && (
          <div className="bg-gray-50 rounded-md p-4 border border-gray-200">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Line Items</h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-500 text-xs uppercase">
                  <th className="text-left pb-2">Code</th>
                  <th className="text-left pb-2">Description</th>
                  <th className="text-right pb-2">Amount</th>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((item, i) => (
                  <tr key={i} className="border-t border-gray-200">
                    <td className="py-2 font-mono font-bold">{item.code}</td>
                    <td className="py-2 text-gray-700">{item.description}</td>
                    <td className="py-2 text-right">R {item.unitPrice.toFixed(2)}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-gray-300 font-bold">
                  <td colSpan={2} className="py-2 text-right">Subtotal</td>
                  <td className="py-2 text-right">R {subtotal.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
          <textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>

        <div className="flex justify-end gap-3">
          <button type="button" onClick={() => navigate('/billing')}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
          <button type="submit" disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
            {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Creating...</> : 'Create Invoice (Draft)'}
          </button>
        </div>
      </form>
    </main>
  );
}
